/**
 * analysisApi — 문제 분석 v2(/api/v2/problem-analysis) + 본문 이력(/api/v2/problem-content).
 *
 * 계약: docs/exam-builder-v2/BE-20260824-analysis-review-screen.md · BE-20260824-problem-hub-v2.md
 * (dev 실검증 완료 — 문제 61385 분석 전 구간 / 61551 이력 전 구간) ·
 * **BE-20260826-audit-fixes-folder-tree-v2apis.md §3**(PATCH 400 신설 · 409 reason · 되돌리기 updated_at).
 * 봉투 { status, message, status_code, data } — 성공 판정 status === 'success'.
 * **409 의 사유는 `data.reason` 에 있다**(문자열 코드). message 는 사람에게 보여 주는 문구라
 * 백엔드가 언제든 다듬는다 — 분기는 reason 으로 한다.
 *
 * 핵심 모델: 분석은 이력이다 — 재분석은 revision 이 쌓이고(덮어쓰기 없음), "확정"은 4슬롯
 * (개념/난이도/정답/풀이)별 포인터다. 과거 revision 을 approve 하면 그게 곧 되돌리기.
 * 화면은 `values`(원본 result + 수정 result_edited 병합 정본)를 그린다.
 */
import { fetchWithTokenRefresh, fetchAPI, AUTH_REDIRECT } from '../../utils/apiUtils';

// 401 표식은 공용 자리(utils/apiUtils)에 하나만 둔다 — 여기서는 그대로 다시 내보낸다
export { AUTH_REDIRECT };

const BASE = '/api/v2/problem-analysis';
const CONTENT = '/api/v2/problem-content';

// 분석 status — **이 저장소의 두 표기가 어긋난다. 여기서 상태 필터를 읽지 마라.**
//  · 이 줄은 도입 때 「100 대기 / 110 대기(큐) / 150 처리중 / 200 완료(검수 대기) / 500 실패」로 적혔다.
//  · problemApi.js 의 ANALYSIS_STATUS(205-219)에는 **110 이 없다** — BE-20260825-reply-batch.md §3-1 이
//    「110 은 OCR 스테이징 M4 값이라 은행 분석(ProblemAnalysis v2)엔 안 나온다」로 handover 표기를
//    정정했기 때문이다. BE-20260824-analysis-review-screen.md §2-3 도 100/150/200/500 만 적는다.
//  · 2026-08-29 회신(BE-20260829-reply-fe-0828.md §1-2)은 목록(`/list`)에 `status` 를 싣는다고만 하고
//    **값 도메인은 주지 않았다.** 그래서 어느 표기가 맞는지 아직 확정이 아니다 — 다음 백엔드 왕복에서
//    물을 것으로 남겼고, 답이 오기 전에는 이 주석으로 **상태 필터 선택지나 폴링 종료 조건을 만들지 않는다**
//    (그래서 fetchAnalysisList 는 필터를 열지 않고, 목록의 폴링은 problemApi.ANALYSIS_POLLING_STATUSES 를 쓴다).
// 표시 헬퍼는 두 곳으로 갈려 있다 — AnalysisReviewCard 는 ocrApi.getAnalysisStatusDisplay 를 100 만
// 보정해 쓰고, 목록은 problemApi.getAnalysisStatusDisplay 를 쓴다(모르는 값이 오면 숫자를 그대로 보인다).
export const ANALYSIS_POLL_MS = 6000; // 실측 건당 중앙값 36초 — 5~8초 간격 권고의 중간값
// ↑ 36초는 BE-20260824-analysis-review-screen.md §2-3 의 dev 실측(건수 미기재)이고 그 뒤로 다시 잰 적이 없다.
//   그래서 화면 문구에서는 숫자를 뺐다(오너 지시 2026-09-28). 새 실측을 받기 전에는 문구에 되살리지 않는다.

function unwrap(res, fallbackMsg) {
  if (!res || res.status !== 'success') {
    throw new Error(res?.message || fallbackMsg);
  }
  return res.data;
}

// 상태코드 분기가 필요한 쓰기 API 공용 — 실패 시 Error.httpStatus 를 실어 throw.
// (ocrApi.requestJsonWithStatus 와 같은 관례 — 그 함수는 미수출이라 로컬로 둔다)
async function requestJsonWithStatus(path, { method = 'POST', payload } = {}, getAccessToken, fallbackMsg) {
  const token = await getAccessToken();
  if (!token) {
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let response;
  try {
    response = await fetchAPI(path, {
      method,
      headers: { Authorization: token, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload ?? {}),
    });
  } catch (netErr) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다
    // (계약 §6: soft-delete 된 사용자의 유효 토큰이 이제 401 이라 이 갈래가 실제로 돌게 됐다).
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return AUTH_REDIRECT;
  }
  let body = null;
  try { body = await response.json(); } catch (_) { /* 아래에서 상태코드로 표면화 */ }
  if (!response.ok || !body || body.status !== 'success') {
    const err = new Error(body?.message || `${fallbackMsg} (${response.status})`);
    err.httpStatus = response.status;
    err.data = body?.data ?? null; // 409 의 data.reason 같은 기계 판별 코드를 화면까지 나른다
    throw err;
  }
  return body.data;
}

// ── 분석 요청 — 201 신규 / 200 이미 진행 중(dedup: 'new'|'already', 연타·동시 사용자 안전) ──
// 409 = 본문 미확정(status≠999).
// options { concept_ids, difficulty, answer, solution } — 키마다 뜻이 셋이다(BE dev d3333ca, 2026-09-27):
//   false = 만들지 않음 · 생략 = 은행에 없을 때만 만듦 · **true = 있어도 다시 만듦**.
//   결과(result·values)의 `skipped_existing` 에 「이미 있어서 건너뛴」 키가 실린다(생략으로 보낸 칸만 생긴다).
//   ⚠ 옛 서버(운영, d3333ca 배포 전)는 true 를 무시하고 생략처럼 처리한다.
//   화면의 분석 창(AnalysisReviewCard·ProblemSearchPanel)은 넷을 늘 명시한다 — analysisTargets.js.
//   넷 다 false 면 400(「최소 한 필드는 켜져 있어야」) — 창이 먼저 막는다.
//   분석 작업 창(AnalysisJobsModal)의 [다시 분석]만 창 없이 바로 돌아 옵션을 싣지 않는다(오너 결정 D2).
// userPrompt(선택) — 사용자가 이 요청에만 다는 지시. 비면 키를 싣지 않는다.
// **수용 확정** — BE-20260829-reply-fe-0828.md §4-1(요청한 9곳 전부 수용·dev 배포) · §4-2:
//   상한 500자(공백 제거 후 기준, 초과 400) · 빈 문자열·공백만은 키를 안 보낸 것과 같다 ·
//   프롬프트 **말미**에 `[추가 지시 — 요청자]` 블록으로 붙고 **옵션·규칙과 충돌하면 옵션·규칙이 이긴다** ·
//   분석 행의 `user_prompt` 로 남아 목록(/list)·상세에 되돌아온다.
// ⚠ 응답이 `dedup:'already'` 면 **이번에 쓴 지시는 적용되지 않는다** — 진행 중이던 행이 그대로
//   끝난다(§4-1). 부르는 쪽은 지시를 실제로 쓴 경우에만 그 사실을 화면에서 말한다
//   (안 쓴 사람에게는 소음이다 — ProblemSearchPanel.askAnalysis · AnalysisReviewCard.handleRequest).
export async function requestAnalysis(problemId, { options, userPrompt } = {}, getAccessToken) {
  const payload = {};
  if (options) payload.options = options;
  if (userPrompt && userPrompt.trim()) payload.user_prompt = userPrompt.trim();
  return requestJsonWithStatus(
    `${BASE}/problems/${problemId}/analysis`,
    { method: 'POST', payload },
    getAccessToken,
    '분석 요청에 실패했습니다.',
  );
}

// ── 현황(문제 기준): { latest: 상세|null, history: [요약], current, state } ──
export async function fetchProblemAnalysis(problemId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${BASE}/problems/${problemId}/analysis`, {}, getAccessToken);
  return unwrap(res, '분석 현황을 불러오지 못했습니다.');
}

// ── 상세 단건: values(정본)·result(원본)·current(은행 현재값)·concept_names·updated_at(낙관락 키) ──
// 2026-09-27(BE d3333ca·12d5567)부터 result 에 셋이 더 실린다 — skipped_existing(이미 있어서 건너뛴 칸) ·
// solution_answer(새 풀이가 도달한 답) · answer_match(은행 정답과 같은가 true/false/null). 읽는 규칙은 analysisTargets.js.
export async function fetchAnalysis(analysisId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${BASE}/analysis/${analysisId}`, {}, getAccessToken);
  return unwrap(res, '분석 상세를 불러오지 못했습니다.');
}

/**
 * 분석 작업 목록 — GET /list?status=&review_status=&page=&per_page= (BE-20260829-reply-fe-0828.md §1-2).
 *
 * 이 목록이 세는 것은 **문제가 아니라 분석 작업**이다. 재분석은 덮어쓰지 않고 revision 이 쌓이므로
 * 같은 problem_id 가 여러 줄로 온다. 검색의 「분석 필요만 보기」(problem-search?needs_analysis=true)와
 * 모집단이 다르다 — 그쪽은 개념·난이도가 빈 **문제**를 세고, 여기는 태운 **작업**을 센다.
 * (같은 회신 §2-5: `problem-analysis/current` 는 **없다**. 「분석 안 된 문제」는 그 needs_analysis 로
 *  흡수됐고 그쪽은 이미 화면에 붙어 있다 — 이 목록이 그 자리를 대신하는 것이 아니다.)
 *
 * 항목 키(회신이 준 그대로): analysis_id · analysis_uuid · problem_id · revision · status ·
 * review_status · user_prompt · edited · model · error · applied_at · created_at · updated_at.
 *
 * ⚠ **status·review_status 의 값 도메인은 회신에 없다.** 그래서 이 함수는 필터 인자를 받기만 하고
 *   화면은 선택지를 만들지 않는다(없는 상태를 고르게 하면 화면이 거짓말을 한다). 값을 받으면 그때 연다.
 *
 * ⚠ 총 건수 키가 회신 안에서 어긋난다 — 봉투는 `{items[], page, per_page, total}` 인데 같은 줄이
 *   「생성/변형 목록과 페이지네이션 동일」이라 말하고, 그쪽(fetchGenerations)은 `total_items`·`total_pages` 다.
 *   **둘 다 견딘다.** 다만 둘 다 없으면 `items.length` 로 채우지 않고 `null`(= 모른다)로 둔다 —
 *   한 페이지 건수를 총계로 말하면 화면이 사실과 다른 숫자를 띄운다. 부르는 쪽은 null 이면 안 그린다.
 */
export async function fetchAnalysisList({ status, reviewStatus, page = 1, perPage = 20 } = {}, getAccessToken) {
  const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
  if (status != null) params.append('status', String(status));
  if (reviewStatus != null) params.append('review_status', String(reviewStatus));
  const res = await fetchWithTokenRefresh(`${BASE}/list?${params}`, {}, getAccessToken);
  const d = unwrap(res, '분석 작업 목록을 불러오지 못했습니다.') || {};
  // 목록 응답 형태 방어(items 별칭·평면 배열) — problemApi.searchProblems 와 같은 관례
  const items = Array.isArray(d) ? d : (Array.isArray(d.items) ? d.items : []);
  const per = d.per_page ?? perPage;
  const totalItems = d.total_items ?? d.total ?? null;
  const totalPages = d.total_pages
    ?? (totalItems != null && per > 0 ? Math.max(1, Math.ceil(totalItems / per)) : null);
  return { items, page: d.page ?? page, perPage: per, totalItems, totalPages };
}

/**
 * 검수 상태 배지 — **분석 계약이 이름을 준 값만 이름으로 부른다.**
 * 100 검수 대기(BE-20260824-analysis-review-screen.md §2-4 상세 예시) · 400 폐기(§2-7 discard).
 * **200 은 분석 계약 어디에도 없다** — 변형·생성 쪽은 200 = 승인이지만(BE-20260825-ai-variation-screens.md §2),
 * 분석도 같은지는 2026-08-29 회신이 값 도메인을 주지 않아 확인되지 않았다. 그래서 이름을 지어내지 않고
 * 숫자를 그대로 보인다 — 모르는 것을 아는 척하는 배지보다 「검수 200」이 정직하다.
 * 색은 형제 구현에서 그대로 가져왔다(variationApi.REVIEW_STATUS).
 */
export const ANALYSIS_REVIEW_STATUS = {
  100: { label: '검수 대기', cls: 'bg-amber-50 text-amber-700' },
  400: { label: '폐기', cls: 'bg-gray-100 text-gray-500' },
};
export function getAnalysisReviewStatusDisplay(reviewStatus) {
  if (reviewStatus == null) return null;
  const m = ANALYSIS_REVIEW_STATUS[reviewStatus];
  return m ? { text: m.label, cls: m.cls } : { text: `검수 ${reviewStatus}`, cls: 'bg-gray-100 text-gray-600' };
}

/**
 * 검수 수정 — PATCH /analysis/{aid}. 보낸 키만 반영. 키에 null = 그 수정 철회(원본 복귀).
 * 원본(result)은 불변. solution 은 blocks|lexical 중 하나만(동시 전송 400).
 *
 * 낙관락 409 — `data.reason:'stale'`(계약 §3). 이전에는 409 만 오고 사유가 없었다.
 *
 * **400 신설(계약 §3 · 감사 C2).** 예전에는 이런 값이 그대로 통과해 승인 때 문제은행에
 * 오형식 풀이가 저장됐다(`{solution_lexical:"abc"}` → has_solution=true 인 못 여는 풀이).
 * 이제 PATCH 단계에서 막힌다 — 화면은 400 을 "무엇이 틀렸는지"로 옮겨 말해야 한다:
 *  - `solution_lexical` 이 객체가 아니거나(스칼라·문자열) `root.children` 이 빈 배열/비배열
 *  - `solution_blocks` 원소가 객체가 아니거나 `kind` 키가 없음
 *  - `difficulty`·`concept_ids` 원소에 **boolean**(true 가 1 로 새어 난이도 1 이 되던 자리)
 * 같은 규칙을 `PATCH /api/v2/problems/{id}` 도 쓴다 — 두 경로가 이제 한 자로 잰다.
 */
export async function patchAnalysis(analysisId, patch, getAccessToken) {
  return requestJsonWithStatus(
    `${BASE}/analysis/${analysisId}`,
    { method: 'PATCH', payload: patch },
    getAccessToken,
    '검수 수정에 실패했습니다.',
  );
}

/**
 * 승인(문제은행 실반영) — fields 로 부분 승인(생략 시 유효값 전부).
 * 응답 { applied[], digest_mismatch, state }. digest_mismatch=true = 분석 후 본문이 바뀜(차단 아님).
 *
 * 409 두 갈래(계약 §3) — `data.reason` 으로 갈린다:
 *  - `'stale'` : expected_updated_at 이 어긋났다(그 사이 누가 고쳤다).
 *  - `'nothing_to_apply'` : **반영할 것이 없다.** 예전에는 실반영 0건이어도 200 "approved" 를
 *    돌려주고 빈 State 행까지 남겨서, 화면은 "반영했습니다"라고 말하는데 은행 값은 그대로인
 *    거짓 보고가 났다(감사 C2-②). 이제 409 다 — 실패가 아니라 **"이미 다 반영돼 있다"**로 안내한다.
 *
 * ⚠ 승인이 성공하면 **`Problems.updated_at` 이 전진한다**(§3 · 감사 C1). 같은 문제의 편집기가
 *   열려 있으면 그 편집기의 expected_updated_at 은 그 순간 낡는다 — 저장이 409 stale 로 막힌다.
 *   승인 뒤 onChanged 를 올려 편집기 쪽이 다시 읽게 하는 것이 이 규약의 나머지 반쪽이다.
 */
export async function approveAnalysis(analysisId, { fields, expectedUpdatedAt } = {}, getAccessToken) {
  const payload = {};
  if (Array.isArray(fields) && fields.length > 0) payload.fields = fields;
  if (expectedUpdatedAt) payload.expected_updated_at = expectedUpdatedAt;
  return requestJsonWithStatus(
    `${BASE}/analysis/${analysisId}/approve`,
    { method: 'POST', payload },
    getAccessToken,
    '승인에 실패했습니다.',
  );
}

// ── 폐기 — review_status→400, 이력은 남는다 ──
export async function discardAnalysis(analysisId, getAccessToken) {
  return requestJsonWithStatus(
    `${BASE}/analysis/${analysisId}/discard`,
    { method: 'POST' },
    getAccessToken,
    '폐기에 실패했습니다.',
  );
}

// ════ 문제 이력 (/api/v2/problem-content) ════
//
// 2026-08-30(계약 `BE-20260830-reply-ocr-repromote-history-close.md` §2)부터 **본문 이력이
// 아니라 문제 이력**이다 — 본문·풀이·정답·라벨·개념·난이도 여섯이 한 줄에 쌓인다.
// 필드별 규칙은 revisionFields.js 에 모았다.

// source — 계약 §2 「판이 생기는 길」 그대로.
// ⚠ **400(분석 승인 apply)이 오래 빠져 있었다.** 계약이 꼽은 여섯 갈래 중 하나인데 이 표에
//   없어서 화면에 「출처 400」이 그대로 떴다. 200(레이아웃 에이전트)은 계약 §2 목록에는
//   없지만 종전부터 있던 값이라 지운다는 근거가 없어 남긴다.
export const CONTENT_SOURCE = {
  100: '편집기',
  200: '레이아웃 에이전트',
  300: 'OCR 등록',
  400: '분석 승인',
  500: '되돌리기',
  900: '도입 전 원본',
};
export function getContentSourceText(source) {
  return CONTENT_SOURCE[source] || `출처 ${source}`;
}

/**
 * 이력 목록 — { items: [{revision, field, field_name, value, source, image_count, created_at}],
 *              heads: {field: 최신 revision}, head_is_live }
 *
 * `head_is_live=false` = 이력 밖 경로로 수정된 상태(최신 판이 현재 본문과 다를 수 있음).
 * 계약 §2 가 이 값은 **종전대로 본문 기준**이라고 못박았다 — 정답·라벨에는 해당하지 않는다.
 *
 * ⚠ 계약의 `?field=` 필터를 **쓰지 않는다.** 여섯 갈래를 오갈 때마다 왕복하게 되고,
 * `heads` 는 어차피 전체 응답에 한 번에 실려 온다. 한 번 받아 화면에서 거른다 —
 * 계약에 페이지네이션이 없어 전체를 받는 것이 종전과 같은 비용이다.
 * 판이 수백으로 늘어 이 판단이 틀리면 그때 이 주석부터 고친다.
 */
export async function fetchContentRevisions(problemId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${CONTENT}/problems/${problemId}/revisions`, {}, getAccessToken);
  return unwrap(res, '문제 이력을 불러오지 못했습니다.');
}

// 판 전문: { content } — 이미지 복원된 Lexical(미리보기용)
export async function fetchContentRevision(problemId, revision, getAccessToken) {
  const res = await fetchWithTokenRefresh(`${CONTENT}/problems/${problemId}/revisions/${revision}`, {}, getAccessToken);
  return unwrap(res, '판 전문을 불러오지 못했습니다.');
}

/**
 * 되돌리기 — POST /problems/{id}/revert. 과거 판을 현재로 복사(새 revision append).
 * 별도 취소 없음(다시 되돌리면 됨).
 *
 * **응답에 `updated_at` 이 추가됐다**(계약 §3 · 감사 C1). 되돌리기가 이제 `Problems.updated_at`
 * 을 전진시키기 때문이다 — 그래서 이 값을 쓰지 않으면 **되돌린 직후의 저장이 항상 409 stale** 이다.
 * 이전에는 되돌리기가 LexicalJson·이력만 갱신하고 Problems 행을 안 건드려서, 열어 둔 편집기가
 * 옛 expected_updated_at 으로 통과해 되돌린 본문을 도로 덮었다.
 *
 * 응답: `{ revision, source, updated_at, … }` — 되돌린 뒤 화면이 할 일 둘 중 하나다:
 *  ① 상세를 다시 읽는다(가장 안전 — 본문·정답·풀이가 함께 새로 온다), 또는
 *  ② 편집기의 expected_updated_at 만 이 `updated_at` 으로 갈아 끼운다.
 * ②는 편집 중인 내용을 지키면서 저장을 살릴 때만 쓴다. 다만 되돌리기는 **본문 자체를 갈아엎는
 * 동작**이라, 편집 중인 본문을 그대로 둔 채 잠금만 푸는 것은 되돌리기를 무효로 만드는 길이다 —
 * 기본은 ①이다.
 */
/**
 * 되돌리기 — **그 판의 field 에만** 적용된다(계약 §2: 「정답 판이면 정답만 되돌림」).
 * 응답 { field, restored_from, new_head_revision, updated_at }.
 * 이미 같은 값이면 200 이고 아무 판도 쌓이지 않는다 — 호출부는 `new_head_revision` 이
 * 직전 head 와 같은지로 그 갈래를 가른다(message 는 이 통로가 버린다).
 */
export async function revertContent(problemId, revision, getAccessToken) {
  return requestJsonWithStatus(
    `${CONTENT}/problems/${problemId}/revert`,
    { method: 'POST', payload: { revision } },
    getAccessToken,
    '되돌리기에 실패했습니다.',
  );
}
